import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { createTeamMember, deleteTeamMember, getTeamMember, getTeamMembers, updateTeamMember } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { TeamMember, TeamMemberRequest } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetAgencyTeams(
  page: number,
  options?: UseQueryOptions<{ data: TeamMember[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: TeamMember[]; meta: MetaApiResponse }>({
    queryKey: ['agencyTeams'],
    queryFn: () => getTeamMembers(page),
    ...options
  });
}

export function useGetAgencyTeam(options?: UseQueryOptions<TeamMember, Error>) {
  const { teamId } = useParams<{ workspaceId: string; teamId: string }>();
  return useQuery<TeamMember>({
    queryKey: ['agencyTeam', teamId],
    queryFn: () => getTeamMember(teamId!),
    enabled: Boolean(teamId),
    ...options
  });
}

export function useCreateOrUpdateAgencyTeam() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ workspaceId: string; teamId: string }>();

  return useMutation({
    mutationFn: (payload: TeamMemberRequest) =>
      teamId ? updateTeamMember(teamId!, payload) : createTeamMember(payload),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['agencyTeam']
      });
      queryClient.invalidateQueries({
        queryKey: ['agencyTeams'],
        refetchType: 'active'
      });
      navigate(-1);
    }
  });
}

export function useDeleteAgencyTeam() {
  return useMutation({
    mutationFn: (id: string) => deleteTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agencyTeams'],
        refetchType: 'active'
      });
    }
  });
}
